import type { ReactNode } from 'react';
import { extractTextFromReactNode } from '@/lib/extractTextFromReactNode';

/**
 * Centralized schema.org JSON-LD builders.
 *
 * Every builder returns a plain object (or array of objects) ready to pass to
 * the `<SEO structuredData>` prop. Keeping the builders here avoids the
 * duplicated `publisher` / breadcrumb / FAQ boilerplate that previously lived
 * inline across guides, tools, stories, and landing pages.
 *
 * Brand constants (URL, logo, social profiles, contact) live here as the single
 * source of truth for structured data.
 */

export const SITE_URL = 'https://codepulsehq.com';

export const ORG = {
  name: 'CodePulse',
  url: SITE_URL,
  logoUrl: `${SITE_URL}/favicon-96x96.png`,
  sameAs: [
    'https://x.com/codepulsehq',
    'https://www.linkedin.com/company/111274263/',
  ],
  supportEmail: 'support@codepulsehq.com',
} as const;

const SCHEMA_CONTEXT = 'https://schema.org';

/**
 * Resolve a path to an absolute URL. The site convention is no trailing slash
 * except the root ("/"), which keeps its slash to match the homepage canonical.
 * Already-absolute URLs are returned unchanged.
 */
export function absoluteUrl(urlPath: string): string {
  if (!urlPath) return SITE_URL;
  if (urlPath.startsWith('http')) return urlPath;
  if (urlPath === '/') return `${SITE_URL}/`;
  return `${SITE_URL}${urlPath.startsWith('/') ? '' : '/'}${urlPath}`;
}

// Shared publisher sub-object reused by every Article-family schema.
const PUBLISHER_ORGANIZATION = {
  '@type': 'Organization',
  name: ORG.name,
  logo: {
    '@type': 'ImageObject',
    url: ORG.logoUrl,
  },
};

/** Standalone Organization node (brand entity for knowledge panels). */
export function buildOrganizationSchema() {
  return {
    '@context': SCHEMA_CONTEXT,
    '@type': 'Organization',
    name: ORG.name,
    url: ORG.url,
    logo: {
      '@type': 'ImageObject',
      url: ORG.logoUrl,
    },
    sameAs: [...ORG.sameAs],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      email: ORG.supportEmail,
    },
  };
}

/**
 * Standalone WebSite node. Intentionally omits `potentialAction`/SearchAction:
 * there is no public site search endpoint, so advertising one would be invalid.
 */
export function buildWebSiteSchema(description?: string) {
  return {
    '@context': SCHEMA_CONTEXT,
    '@type': 'WebSite',
    name: ORG.name,
    url: ORG.url,
    ...(description ? { description } : {}),
  };
}

export interface ArticleSchemaInput {
  headline: string;
  description?: string;
  datePublished?: string;
  dateModified?: string;
  /** Author display name. Defaults to the CodePulse founder for Person authors. */
  authorName?: string;
  authorJobTitle?: string;
  authorUrl?: string;
  /** 'Person' (default) or 'Organization' (e.g. "CodePulse Research"). */
  authorType?: 'Person' | 'Organization';
  /** Canonical path of the article, e.g. "/guides/foo". */
  urlPath: string;
  /** Optional hero/OG image (path or absolute URL). */
  image?: string;
}

function buildAuthor(input: ArticleSchemaInput) {
  if (input.authorType === 'Organization') {
    return { '@type': 'Organization', name: input.authorName || ORG.name };
  }
  return {
    '@type': 'Person',
    name: input.authorName || 'Ashley Russell',
    jobTitle: input.authorJobTitle || 'Founder, CodePulse',
    url: input.authorUrl || 'https://www.linkedin.com/in/its-ash/',
  };
}

/** Article schema with CodePulse publisher + mainEntityOfPage. */
export function buildArticleSchema(input: ArticleSchemaInput) {
  return {
    '@context': SCHEMA_CONTEXT,
    '@type': 'Article',
    headline: input.headline,
    ...(input.description ? { description: input.description } : {}),
    ...(input.datePublished ? { datePublished: input.datePublished } : {}),
    ...(input.dateModified ? { dateModified: input.dateModified } : {}),
    author: buildAuthor(input),
    publisher: PUBLISHER_ORGANIZATION,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': absoluteUrl(input.urlPath),
    },
    ...(input.image ? { image: absoluteUrl(input.image) } : {}),
  };
}

export interface BreadcrumbItem {
  name: string;
  /** Omit on the current/last crumb (Google convention). */
  urlPath?: string;
}

export function buildBreadcrumbSchema(items: BreadcrumbItem[]) {
  return {
    '@context': SCHEMA_CONTEXT,
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      ...(item.urlPath ? { item: absoluteUrl(item.urlPath) } : {}),
    })),
  };
}

export interface FAQItem {
  question: string;
  answer: string | ReactNode;
}

/**
 * FAQPage schema. JSX answers are flattened to plain text so the JSON-LD `text`
 * field is never empty (empty text fails Google Rich Results validation).
 */
export function buildFAQPageSchema(faqs: FAQItem[]) {
  return {
    '@context': SCHEMA_CONTEXT,
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          typeof faq.answer === 'string'
            ? faq.answer
            : extractTextFromReactNode(faq.answer),
      },
    })),
  };
}

export interface HowToSchemaInput {
  name: string;
  steps: string[];
}

/** HowTo schema from an ordered list of step strings. */
export function buildHowToSchema(input: HowToSchemaInput) {
  return {
    '@context': SCHEMA_CONTEXT,
    '@type': 'HowTo',
    name: input.name,
    step: input.steps.map((text, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      text,
    })),
  };
}

export interface ItemListEntry {
  name: string;
  description?: string;
  urlPath: string;
}

export interface ItemListSchemaInput {
  name: string;
  description?: string;
  /** Type of each listed item. Defaults to SoftwareApplication (free tool). */
  itemType?: 'SoftwareApplication' | 'Article';
  items: ItemListEntry[];
}

export function buildItemListSchema(input: ItemListSchemaInput) {
  const itemType = input.itemType ?? 'SoftwareApplication';
  return {
    '@context': SCHEMA_CONTEXT,
    '@type': 'ItemList',
    name: input.name,
    ...(input.description ? { description: input.description } : {}),
    numberOfItems: input.items.length,
    itemListElement: input.items.map((entry, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item:
        itemType === 'Article'
          ? {
              '@type': 'Article',
              name: entry.name,
              ...(entry.description ? { description: entry.description } : {}),
              url: absoluteUrl(entry.urlPath),
            }
          : {
              '@type': 'SoftwareApplication',
              name: entry.name,
              ...(entry.description ? { description: entry.description } : {}),
              url: absoluteUrl(entry.urlPath),
              applicationCategory: 'DeveloperApplication',
              operatingSystem: 'Web',
              offers: {
                '@type': 'Offer',
                price: '0',
                priceCurrency: 'USD',
              },
            },
    })),
  };
}

export interface SoftwareApplicationSchemaInput {
  name: string;
  description: string;
  urlPath: string;
  featureList?: string[];
  offers?: object | object[];
  /** Include a `provider` Organization node. */
  includeProvider?: boolean;
}

export function buildSoftwareApplicationSchema(input: SoftwareApplicationSchemaInput) {
  return {
    '@context': SCHEMA_CONTEXT,
    '@type': 'SoftwareApplication',
    name: input.name,
    description: input.description,
    url: absoluteUrl(input.urlPath),
    applicationCategory: 'DeveloperApplication',
    operatingSystem: 'Web',
    ...(input.offers ? { offers: input.offers } : {}),
    ...(input.featureList ? { featureList: input.featureList } : {}),
    ...(input.includeProvider
      ? { provider: { '@type': 'Organization', name: ORG.name, url: ORG.url } }
      : {}),
  };
}

/** A single free Offer (price 0, USD) — the common case for tools. */
export const FREE_OFFER = {
  '@type': 'Offer',
  price: '0',
  priceCurrency: 'USD',
} as const;

export interface PricingTierLike {
  name: string;
  price_monthly: number;
  price_yearly: number;
}

/**
 * Build an Offer[] from the same pricing tiers the PricingPage renders.
 * Prices are never hardcoded here — pass the live tiers (e.g.
 * `Object.values(pricing)`) so schema stays in sync with `get_tier_pricing()`.
 */
export function buildPricingOffers(
  tiers: PricingTierLike[],
  billingPeriod: 'monthly' | 'yearly',
  priceCurrency = 'USD',
) {
  return tiers.map((tier) => ({
    '@type': 'Offer',
    name: tier.name,
    price: String(billingPeriod === 'yearly' ? tier.price_yearly : tier.price_monthly),
    priceCurrency,
  }));
}

/** Map a ResearchArticle metadata entry to Article schema. */
export interface ResearchArticleLike {
  slug: string;
  title: string;
  metaDescription?: string;
  description?: string;
  publishedDate?: string;
  updatedAt?: string;
  author?: string;
  ogImage?: string;
}

export function buildResearchArticleSchema(article: ResearchArticleLike) {
  return buildArticleSchema({
    headline: article.title,
    description: article.metaDescription || article.description,
    datePublished: article.publishedDate,
    dateModified: article.updatedAt,
    authorName: article.author || 'CodePulse Research',
    authorType: 'Organization',
    urlPath: `/research/${article.slug}`,
    image: article.ogImage,
  });
}
